import {
  Element,
  InstallSpec,
  ItemComponent,
  Option,
} from "@p0security/shared/install/types";

export const ConnectorHosting = Element({
  label: "Connector hosting",
  description:
    "The cloud account P0 uses to invoke your custom connector deployment",
  type: "select",
  step: "new",
  default: "aws",
  options: {
    aws: Option({
      maturity: "preview" as const,
      label: "AWS Lambda",
      schema: {
        accountId: Element({
          label: "AWS account ID",
          description:
            "The AWS account in which your custom connector's Lambda function is deployed",
          type: "dynamic",
          step: "new",
        }),
        connectorName: Element({
          label: "Connector name",
          description:
            "The name of the Lambda function hosting your custom connector",
          type: "string",
          step: "new",
        }),
        connectorRegion: Element({
          label: "Connector region",
          description:
            "The AWS region in which your custom connector's Lambda function is deployed",
          type: "string",
          step: "new",
        }),
      },
    }),
    gcp: Option({
      maturity: "preview",
      label: "Google Cloud Run",
      schema: {
        projectId: Element({
          label: "GCP project ID",
          description:
            "The GCP project in which your custom connector's Cloud Run service is deployed",
          type: "dynamic",
          step: "new",
        }),
        connectorName: Element({
          label: "Connector name",
          description:
            "The name of the Cloud Run service hosting your custom connector",
          type: "string",
          step: "new",
        }),
        connectorRegion: Element({
          label: "Connector region",
          description:
            "The GCP region in which your custom connector's Cloud Run service is deployed",
          type: "string",
          step: "new",
        }),
        // Resolved and persisted at install time — the Cloud Run URL isn't derivable like the AWS ARN.
        connectorServiceUri: Element({
          label: "Connector service URI",
          description:
            "The invocation URL of the connector's Cloud Run service, resolved once at install time",
          type: "generated",
        }),
      },
    }),
  },
});

/** What to enter when {@link isConnectorUrlOrBlank} refuses a value. */
export const INVALID_CONNECTOR_URL =
  "Enter an https URL with only a host and an optional port, such as https://connector.example.com, with no path or trailing slash.";

/** Whether `url` can fill the optional connector URL: blank, or an `https`
 * origin with nothing after it. The install framework runs a field's
 * validator on any string it's sent, so a blank value has to pass. */
const isConnectorUrlOrBlank = (url: unknown): url is string => {
  if (typeof url !== "string") return false;
  if (url.trim() === "") return true;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" && parsed.origin === url;
  } catch {
    return false;
  }
};

/** {@link ConnectorHosting} plus Azure Container Apps, for a custom
 * application only.
 *
 * Other integrations reuse {@link ConnectorHosting} for connectors that P0
 * builds, which can't run on Azure until each one is ported, so the `azure`
 * option isn't on it. The option is in dev maturity until the connector SDK
 * can build an Azure image (ENG-5381).
 */
export const AppConnectorHosting = Element({
  ...ConnectorHosting,
  options: {
    ...ConnectorHosting.options,
    gcp: Option({
      ...ConnectorHosting.options.gcp,
      schema: {
        ...ConnectorHosting.options.gcp.schema,
        connectorUrl: Element({
          label: "Connector URL",
          description:
            "The URL P0 calls the connector at, such as your own load balancer in front of the Cloud Run service. Leave it empty to call the service's run.app URL.",
          type: "string",
          placeholder: "https://connector.example.com",
          optional: true,
          step: "new",
          validator: async (_id, url) =>
            isConnectorUrlOrBlank(url) ? undefined : INVALID_CONNECTOR_URL,
        }),
      },
    }),
    azure: Option({
      maturity: "dev",
      label: "Azure Container Apps",
      schema: {
        // Also selects the `azure` root install whose service account P0
        // invokes the connector as.
        directoryId: Element({
          label: "Tenant ID",
          description:
            "The Microsoft Entra directory (tenant) in which your custom connector's Container App is deployed",
          type: "dynamic",
          step: "new",
        }),
        subscriptionId: Element({
          label: "Subscription ID",
          description:
            "The Azure subscription in which your custom connector's Container App is deployed",
          type: "string",
          step: "new",
        }),
        resourceGroup: Element({
          label: "Resource group",
          description:
            "The resource group in which your custom connector's Container App is deployed",
          type: "string",
          step: "new",
        }),
        connectorName: Element({
          label: "Connector name",
          description:
            "The name of the Container App hosting your custom connector",
          type: "string",
          step: "new",
        }),
        connectorRegion: Element({
          label: "Connector region",
          description:
            "The Azure region in which your custom connector's Container App is deployed",
          type: "string",
          step: "new",
        }),
        // Azure generates both, so they're read from the Container App at
        // install time and persisted.
        connectorServiceUri: Element({
          label: "Connector service URI",
          description:
            "The URL of the connector's Container App, resolved once at install time",
          type: "generated",
        }),
        connectorClientId: Element({
          label: "Connector client ID",
          description:
            "The client ID of the connector's app registration, resolved once at install time",
          type: "generated",
        }),
      },
    }),
  },
});

export const AppComponents = InstallSpec({
  "iam-write": ItemComponent({
    label: "Custom application",
    description:
      "Register a deployment of your custom connector and request access through it.",
    type: "string",
    maturity: "preview",
    schema: {
      hosting: AppConnectorHosting,
    },
  }),
});
